import { useEffect } from 'react';

type Shortcut = { keys: string[]; label: string };
const GROUPS: { title: string; items: Shortcut[] }[] = [
  {
    title: 'Anywhere',
    items: [
      { keys: ['n'], label: 'Quick add (Shift+Enter for bulk)' },
      { keys: ['⌘', 'K'], label: 'Search' },
      { keys: ['g', 'i'], label: 'Go → Inbox' },
      { keys: ['g', 't'], label: 'Go → Today' },
      { keys: ['g', 'n'], label: 'Go → Next' },
      { keys: ['g', 'w'], label: 'Go → Waiting' },
      { keys: ['g', 's'], label: 'Go → Scheduled' },
      { keys: ['g', 'm'], label: 'Go → Someday' },
      { keys: ['g', 'l'], label: 'Go → Logbook' },
      { keys: ['g', 'p'], label: 'Go → Projects' },
      { keys: ['?'], label: 'This cheat sheet' },
      { keys: ['Esc'], label: 'Close dialog' },
    ],
  },
  {
    title: 'In a task list',
    items: [
      { keys: ['j', '↓'], label: 'Next row' },
      { keys: ['k', '↑'], label: 'Previous row' },
      { keys: ['Enter', 'o'], label: 'Open task' },
      { keys: ['Space', 'x'], label: 'Toggle complete' },
      { keys: ['s'], label: 'Toggle focus star (Today)' },
      { keys: ['e'], label: 'Open editor (same as Enter)' },
      { keys: ['Del'], label: 'Delete (with confirm)' },
      { keys: ['1'], label: 'Status → Inbox' },
      { keys: ['2'], label: 'Status → Next' },
      { keys: ['3'], label: 'Status → Waiting' },
      { keys: ['4'], label: 'Status → Scheduled' },
      { keys: ['5'], label: 'Status → Someday' },
      { keys: ['6'], label: 'Status → Done' },
    ],
  },
];

export function HelpOverlay({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === '?') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 grid place-items-center px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-zinc-900 border border-zinc-800 rounded-2xl p-4 md:p-6 animate-pop max-h-[90dvh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Keyboard shortcuts</h2>
          <kbd className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">Esc</kbd>
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          Mostly for the people who would have used vim if given the chance.
        </p>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
          {GROUPS.map((g) => (
            <section key={g.title}>
              <h3 className="text-xs uppercase tracking-wider text-zinc-500 mb-2">{g.title}</h3>
              <ul className="space-y-1.5">
                {g.items.map((s) => (
                  <li key={s.label} className="flex items-center gap-3 text-sm">
                    <span className="flex items-center gap-1 shrink-0">
                      {s.keys.map((k, i) => (
                        <span key={i} className="flex items-center gap-1">
                          {i > 0 && <span className="text-zinc-600 text-xs">then</span>}
                          <kbd className="rounded bg-zinc-800 border border-zinc-700 px-1.5 py-0.5 text-xs font-mono">
                            {k}
                          </kbd>
                        </span>
                      ))}
                    </span>
                    <span className="text-zinc-300">{s.label}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
